import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Collapse,
  ListItemIcon,
  ListItemButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Devices';
import SchoolIcon from '@mui/icons-material/School';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ShieldIcon from '@mui/icons-material/Shield';

const Navbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  
  // State for desktop dropdown menus
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  
  // State for mobile nested menus
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };
  
  const handleMenuEnter = (event: React.MouseEvent<HTMLElement>, menuId: string) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuId);
  };

  const handleMenuLeave = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };
  
  const toggleMobileSubmenu = (menuId: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Main menu structure with submenus
  const menuItems = [
    { 
      text: 'Home', 
      path: '/' 
    },
    { 
      text: 'About Us', 
      path: '/about' 
    },
    { 
      text: 'CMMI', 
      path: '/services/cmmi',
      id: 'cmmi-menu',
      icon: <BusinessIcon fontSize="small" />,
      subItems: [
        { text: 'CMMI Consultancy', path: '/services/cmmi/consultancy' },
        { text: 'CMMI Appraisal', path: '/services/cmmi/appraisal' }
      ]
    },
    { 
      text: 'CMMC', 
      path: '/services/cmmc',
      id: 'cmmc-menu',
      icon: <ShieldIcon fontSize="small" />,
      subItems: [
        { text: 'CMMC Consultancy', path: '/services/cmmc/consultancy' },
        { text: 'CMMC Assessment', path: '/services/cmmc/assessment' }
      ]
    },
    { 
      text: 'ISO', 
      path: '/services/iso',
      id: 'iso-menu',
      icon: <SecurityIcon fontSize="small" />,
      subItems: [
        { text: 'ISO 9001:2015 Services', path: '/services/iso/iso9001' },
        { text: 'ISO 27001:2022 Services', path: '/services/iso/iso27001' },
        { text: 'ISO 42001:2023 Services', path: '/services/iso/iso42001' },
        { text: 'ISO 20000-1:2018 Services', path: '/services/iso/iso20000' },
        { text: 'ISO 22301:2019 Services', path: '/services/iso/iso22301' },
        { text: 'ISO 45001:2018 Services', path: '/services/iso/iso45001' },
        { text: 'ISO 31000:2018 Services', path: '/services/iso/iso31000' },
      ]
    },
    { 
      text: 'Digital Services', 
      path: '/services/digital',
      id: 'digital-menu',
      icon: <DevicesIcon fontSize="small" />,
      subItems: [
        { text: 'Digital Transformation', path: '/services/digital/transformation' },
        { 
          text: 'Software Development', 
          path: '/services/digital/software',
          nestedItems: [
            { text: 'Desktop Development', path: '/services/digital/desktop' },
            { text: 'Mobile App Development', path: '/services/digital/mobile' },
            { text: 'Web Development', path: '/services/digital/web' },
            { text: 'Quality Assurance Services', path: '/services/digital/qa' },
          ]
        },
      ]
    },
    { 
      text: 'Training', 
      path: '/services/training',
      icon: <SchoolIcon fontSize="small" />,
    },
    { 
      text: 'Other Services', 
      path: '/services/other',
      id: 'other-menu',
      icon: <MoreHorizIcon fontSize="small" />,
      subItems: [
        { text: 'Semiconductor Services', path: '/services/semiconductor' },
        { text: 'Data Entry Services', path: '/services/other/data-entry' },
        { text: 'HR Outsourcing Services', path: '/services/other/hr-outsourcing' },
        { text: 'Research & Development', path: '/services/other/research' },
        { text: 'Managed Services for Telecom Sector', path: '/services/other/telecom' },
        { text: 'HIPAA Compliance Services', path: '/services/other/hipaa' },
        { text: 'SOC 1 & 2 Services', path: '/services/other/soc' },
      ]
    },
    { 
      text: 'Contact Us', 
      path: '/contact' 
    },
  ];

  // Mobile drawer content
  const drawer = (
    <List sx={{ width: 280 }}>
      {menuItems.map((item) => (
        <React.Fragment key={item.text}>
          {item.subItems ? (
            <>
              <ListItem 
                disablePadding
                sx={{ 
                  color: location.pathname === item.path ? 'primary.main' : 'inherit',
                  fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                }}
              >
                <ListItemButton 
                  onClick={() => toggleMobileSubmenu(item.id || item.text)}
                  sx={{ py: 1.5 }}
                >
                  {item.icon && <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>}
                  <ListItemText primary={item.text} />
                  {openMenus[item.id || item.text] ? 
                    <KeyboardArrowDownIcon /> : 
                    <KeyboardArrowRightIcon />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openMenus[item.id || item.text]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <React.Fragment key={subItem.text}>
                      {subItem.nestedItems ? (
                        <>
                          <ListItem 
                            disablePadding
                            sx={{ pl: 4 }}
                          >
                            <ListItemButton 
                              onClick={() => toggleMobileSubmenu(subItem.text)}
                              sx={{ py: 1.5 }}
                            >
                              <ListItemText primary={subItem.text} />
                              {openMenus[subItem.text] ? 
                                <KeyboardArrowDownIcon /> : 
                                <KeyboardArrowRightIcon />}
                            </ListItemButton>
                          </ListItem>
                          <Collapse in={openMenus[subItem.text]} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {subItem.nestedItems.map((nestedItem) => (
                                <ListItem 
                                  disablePadding
                                  key={nestedItem.text}
                                  sx={{ pl: 6 }}
                                >
                                  <ListItemButton 
                                    component={Link} 
                                    to={nestedItem.path}
                                    onClick={handleDrawerToggle}
                                    sx={{ py: 1.5 }}
                                  >
                                    <ListItemText primary={nestedItem.text} />
                                  </ListItemButton>
                                </ListItem>
                              ))}
                            </List>
                          </Collapse>
                        </>
                      ) : (
                        <ListItem 
                          disablePadding
                          sx={{ pl: 4 }}
                        >
                          <ListItemButton
                            component={Link} 
                            to={subItem.path}
                            onClick={handleDrawerToggle}
                            sx={{ py: 1.5 }}
                          >
                            <ListItemText primary={subItem.text} />
                          </ListItemButton>
                        </ListItem>
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Collapse>
            </>
          ) : (
            <ListItem 
              disablePadding
              sx={{ 
                color: location.pathname === item.path ? 'primary.main' : 'inherit',
                fontWeight: location.pathname === item.path ? 'bold' : 'normal',
              }}
            >
              <ListItemButton
                component={Link} 
                to={item.path}
                onClick={handleDrawerToggle}
                sx={{ py: 1.5 }}
              >
                {item.icon && <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>}
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          )}
        </React.Fragment>
      ))}
    </List>
  );

  return (
    <AppBar position="static" elevation={0} sx={{ 
      boxShadow: '0 4px 20px rgba(0,0,0,0.25), 0 2px 5px rgba(0,20,80,0.15), 0 0 15px rgba(25,118,210,0.1)',
      background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 50%, #0c2340 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: 'linear-gradient(90deg, rgba(25,118,210,0.2), rgba(25,118,210,0.8), rgba(25,118,210,0.2))',
        boxShadow: '0 0 10px rgba(25,118,210,0.5)',
        zIndex: 1,
        animation: 'gradient 8s ease infinite',
        backgroundSize: '200% auto',
        '@keyframes gradient': {
          '0%': { backgroundPosition: '0% center' },
          '50%': { backgroundPosition: '100% center' },
          '100%': { backgroundPosition: '0% center' },
        },
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
      },
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 6px 25px rgba(0,0,0,0.3), 0 3px 7px rgba(0,20,80,0.2), 0 0 20px rgba(25,118,210,0.15)',
      }
    }}>
      <Toolbar sx={{ px: { xs: 2, md: 4 }, py: { xs: 1, md: 1.5 }, position: 'relative' }}>
        {/* Decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0,
          opacity: 0.15,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -50,
            left: -50,
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 70%)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -30,
            right: '30%',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 70%)',
          }
        }} />
        
        <Box
          component={Link}
          to="/"
          sx={{ 
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            zIndex: 1,
            position: 'relative'
          }}
        >
          <img 
            src="/Logo.png" 
            alt="Right Track Technologies Limited Logo" 
            style={{ 
              height: isMobile ? '50px' : '65px', 
              width: isMobile ? '180px' : '230px',
              marginRight: '10px',
              padding: '5px',
              backgroundColor: 'transparent',
              borderRadius: '4px',
              filter: 'drop-shadow(0 0 5px rgba(255,255,255,0.2))'
            }}
          />
        </Box>
        {isMobile ? (
          <>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                padding: '12px',
                marginLeft: '8px'
              }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              variant="temporary"
              anchor="right"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true,
              }}
              sx={{
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: 280,
                },
              }}
            >
              {drawer}
            </Drawer>
          </>
        ) : (
          <Box sx={{ display: 'flex' }}>
            {menuItems.map((item) => (
              <React.Fragment key={item.text}>
                {item.subItems ? (
                  <>
                    <Button
                      color="inherit"
                      aria-haspopup="true"
                      onMouseEnter={(e) => handleMenuEnter(e, item.id || item.text)}
                      endIcon={<KeyboardArrowDownIcon />}
                      sx={{ 
                        mx: 0.5,
                        px: 1,
                        minWidth: 'auto',
                        position: 'relative',
                        '&:after': activeMenu === (item.id || item.text) ? {
                          content: '""',
                          position: 'absolute',
                          width: '100%',
                          height: '3px',
                          bgcolor: 'secondary.main',
                          bottom: 0,
                          left: 0,
                          borderTopLeftRadius: '3px',
                          borderTopRightRadius: '3px',
                        } : {}
                      }}
                    >
                      {item.text}
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={activeMenu === (item.id || item.text)}
                      onClose={handleMenuClose}
                      disableAutoFocus
                      disableRestoreFocus
                      MenuListProps={{
                        'aria-labelledby': `${item.id || item.text}-button`,
                        onMouseLeave: handleMenuLeave
                      }}
                      sx={{ 
                        '& .MuiPaper-root': { 
                          minWidth: 200,
                          boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                        }
                      }}
                      disableAutoFocusItem
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      {item.subItems.flatMap((subItem) =>
                        subItem.nestedItems ? [
                          <MenuItem
                            key={subItem.text}
                            component={Link}
                            to={subItem.path}
                            onClick={handleMenuClose}
                          >
                            {subItem.text}
                          </MenuItem>,
                          <Box key={`${subItem.text}-nested`} sx={{ pl: 2, borderLeft: `2px solid ${theme.palette.primary.main}`, ml: 2 }}>
                            {subItem.nestedItems.map((nestedItem) => (
                              <MenuItem
                                key={nestedItem.text}
                                component={Link}
                                to={nestedItem.path}
                                onClick={handleMenuClose}
                                sx={{ fontSize: '0.9rem' }}
                              >
                                {nestedItem.text}
                              </MenuItem>
                            ))}
                          </Box>,
                        ] : [
                          <MenuItem
                            key={subItem.text}
                            component={Link}
                            to={subItem.path}
                            onClick={handleMenuClose}
                          >
                            {subItem.text}
                          </MenuItem>,
                        ]
                      )}
                    </Menu>
                  </>
                ) : (
                  <Button
                    color="inherit"
                    component={Link}
                    to={item.path}
                    sx={{ 
                      mx: 0.5,
                      px: 1,
                      minWidth: 'auto',
                      position: 'relative',
                      '&:after': location.pathname === item.path ? {
                        content: '""',
                        position: 'absolute',
                        width: '100%',
                        height: '3px',
                        bgcolor: 'secondary.main',
                        bottom: 0,
                        left: 0,
                        borderTopLeftRadius: '3px',
                        borderTopRightRadius: '3px',
                      } : {}
                    }}
                  >
                    {item.text}
                  </Button>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 